<template>
  <v-dialog v-model="openMode" max-width="600px" persistent transition="dialog-bottom-transition">
    <v-card class="api-dialog-card">
      <v-card-title class="dialog-header">
        <v-icon class="ml-2" dark>mdi-webhook</v-icon>
        <span>{{ itemToEdit.action_name }}</span>
        <v-spacer></v-spacer>
        <v-btn icon dark @click="cancelEdit">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="pt-4">
        <v-form @submit.prevent="saveEditedApi" ref="form">
          <!-- Status Toggle -->
          <div class="setting-section">
            <div class="setting-header">
              <v-icon color="#0d2c6d" class="mr-2">mdi-toggle-switch</v-icon>
              <span class="setting-title">סטטוס:</span>
            </div>
            <div class="d-flex align-center mt-2">
              <v-switch v-model="itemEdited.mode" color="success" inset hide-details class="ma-0"></v-switch>
              <span class="mr-2 status-text" :class="itemEdited.mode ? 'success--text' : 'error--text'">
                {{ itemEdited.mode ? 'פעיל' : 'מושבת' }}
              </span>
            </div>
          </div>

          <!-- Webhook URL -->
          <div class="setting-section">
            <div class="setting-header">
              <v-icon color="#0d2c6d" class="mr-2">mdi-link</v-icon>
              <span class="setting-title">כתובת Webhook:</span>
            </div>
            <v-text-field v-model="itemEdited.url" outlined dense hide-details="auto" label="הזן כתובת URL מלאה"
              placeholder="https://example.com/webhook" prepend-inner-icon="mdi-web" class="mt-2" :rules="[
                v => !v || isValidUrl(v) || 'נא להזין כתובת URL תקינה'
              ]"></v-text-field>
            <div class="caption text-right mt-1 grey--text">
              הכתובת חייבת להתחיל ב-http:// או https://
            </div>
          </div>

          <!-- Added Headers section -->
          <div class="setting-section">
            <div class="setting-header">
              <v-icon color="#0d2c6d" class="mr-2">mdi-code-tags</v-icon>
              <span class="setting-title">Headers:</span>
            </div>

            <!-- Headers list -->
            <div v-if="headersList.length > 0" class="mt-2">
              <v-card v-for="(header, index) in headersList" :key="index" outlined class="mb-2 header-card">
                <v-card-text class="pa-2">
                  <v-row dense align="center">
                    <v-col cols="5">
                      <v-text-field v-model="header.key" outlined dense hide-details label="שם Header"
                        placeholder="Content-Type"></v-text-field>
                    </v-col>
                    <v-col cols="5">
                      <v-text-field v-model="header.value" outlined dense hide-details label="ערך"
                        placeholder="application/json"></v-text-field>
                    </v-col>
                    <v-col cols="2" class="text-center">
                      <v-btn icon small color="error" @click="removeHeader(index)">
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </div>

            <!-- Add header button -->
            <v-btn text color="primary" @click="addHeader" class="mt-2" small>
              <v-icon left small>mdi-plus</v-icon>
              הוסף Header
            </v-btn>

            <div class="caption text-right mt-1 grey--text">
              הוסף headers מותאמים אישית לבקשת ה-webhook
            </div>
          </div>

          <!-- Action Information -->
          <div class="setting-section info-section">
            <div class="setting-header">
              <v-icon color="#0d2c6d" class="mr-2">mdi-information</v-icon>
              <span class="setting-title">פרטי הפעולה:</span>
            </div>
            <div class="mt-2 info-content">
              <div class="d-flex mb-1">
                <span class="info-label">שם הפעולה:</span>
                <span class="info-value">{{ itemToEdit.action_name }}</span>
              </div>
              <div class="d-flex mb-1" v-if="itemToEdit.action">
                <span class="info-label">פעולה:</span>
                <span class="info-value">{{ itemToEdit.action }}</span>
              </div>
              <div class="d-flex" v-if="itemToEdit.exten">
                <span class="info-label">שלוחה:</span>
                <span class="info-value">{{ itemToEdit.exten }}</span>
              </div>
            </div>
          </div>
        </v-form>

        <!-- Loading Indicator -->
        <v-row v-if="progressShow" class="my-2">
          <v-col class="d-flex justify-center">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="dialog-actions">
        <v-spacer></v-spacer>
        <v-btn text @click="cancelEdit" class="cancel-button" :disabled="progressShow">
          <v-icon left>mdi-close</v-icon>
          ביטול
        </v-btn>
        <v-btn color="primary" @click="saveEditedApi" class="save-button" :loading="progressShow"
          :disabled="progressShow">
          <v-icon left>mdi-content-save</v-icon>
          שמור שינויים
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Notification -->
    <SnackBar v-model="snackbar" :snacbarText="snacbarText" :snackbarColorBt="snackbarColorBt"></SnackBar>
  </v-dialog>
</template>

<script>
import ApiServices from '@/services/api.service'
import SnackBar from '@/components/widgets/snackBar.vue'

export default {
  props: {
    itemToEdit: Object,
    value: { type: Boolean, default: false },
  },
  components: {
    SnackBar,
  },
  data: () => ({
    progressShow: false,
    snackbar: false,
    snackbarColorBt: "success",
    snacbarText: "",
    itemEdited: {
      _id: "",
      action_name: "",
      action: "",
      exten: "",
      mode: false,
      url: "",
      headers: {}, // Added headers object
    },
    headersList: [], // Array to manage headers in UI
  }),
  methods: {
    addHeader() {
      this.headersList.push({ key: '', value: '' });
    },

    removeHeader(index) {
      this.headersList.splice(index, 1);
    },

    prepareHeadersObject() {
      const headersObj = {};
      this.headersList.forEach(header => {
        if (header.key && header.key.trim() !== '') {
          headersObj[header.key.trim()] = header.value;
        }
      });
      return headersObj;
    },

    loadHeadersFromObject(headersObj) {
      if (headersObj && typeof headersObj === 'object') {
        this.headersList = Object.keys(headersObj).map(key => ({
          key: key,
          value: headersObj[key]
        }));
      } else {
        this.headersList = [];
      }
    },

    async saveEditedApi() {
      if (!this.$refs.form.validate()) {
        return;
      }

      try {
        this.progressShow = true;
        this.itemEdited.headers = this.prepareHeadersObject();

        const datesJSON = JSON.stringify({ item: this.itemEdited });
        let token = localStorage.getItem("token");
        let api = process.env.VUE_APP_BASE_URL + "/settings/edit_api_integration";

        const res = await fetch(api, ApiServices.requestOptions("PUT", datesJSON, token));
        const jsonObject = await res.json();

        if (res.status >= 400) {
          this.$emit("apiEdited", "", "error: " + jsonObject, "error");
        } else if (res.status === 200) {
          this.$emit("apiEdited", this.itemEdited, "השינויים נשמרו בהצלחה!", "success");
          this.openMode = false;
        }
      } catch (error) {
        this.showSnackBar("Error saved list: " + error, "error");
        this.$emit("apiEdited", "", "שגיאה בשמירת השינויים", "error");
      } finally {
        this.progressShow = false;
      }
    },

    cancelEdit() {
      this.openMode = false;
    },

    showSnackBar(message, color) {
      this.snacbarText = message;
      this.snackbarColorBt = color === "error" ? "red" : "green";
      this.snackbar = true;
    },

    isValidUrl(url) {
      if (!url) return true;
      try {
        new URL(url);
        return true;
      } catch (e) {
        return false;
      }
    },
  },
  computed: {
    openMode: {
      get() {
        return this.value;
      },
      set(v) {
        return this.$emit('input', v);
      }
    },
  },
  watch: {
    itemToEdit: {
      handler(newVal) {
        this.itemEdited = JSON.parse(JSON.stringify(newVal));
        this.loadHeadersFromObject(this.itemEdited.headers);
      },
      immediate: true,
      deep: true
    }
  },
  mounted() {
    this.itemEdited = JSON.parse(JSON.stringify(this.itemToEdit));
    this.loadHeadersFromObject(this.itemEdited.headers);
  },
}
</script>

<style scoped>
.api-dialog-card {
  border-radius: 8px;
  overflow: hidden;
  direction: rtl;
  font-family: 'Heebo', sans-serif;
}

.dialog-header {
  background: linear-gradient(135deg, #0d2c6d 0%, #1a4ba5 100%);
  color: white;
  font-size: 1.25rem;
  font-weight: 600;
  padding: 16px;
}

.setting-section {
  padding: 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.setting-section:last-child {
  border-bottom: none;
}

.setting-header {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.setting-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #0d2c6d;
}

.status-text {
  font-weight: 500;
}

.info-section {
  background-color: rgba(0, 0, 0, 0.02);
}

.info-content {
  padding: 8px 12px;
  border-radius: 4px;
  background-color: rgba(0, 0, 0, 0.03);
}

.info-label {
  font-weight: 600;
  min-width: 100px;
  color: #0d2c6d;
}

.info-value {
  font-weight: 400;
  color: #333;
}

/* Added styles for headers section */
.header-card {
  border: 1px solid rgba(13, 44, 109, 0.2);
  border-radius: 4px;
  transition: all 0.2s ease;
}

.header-card:hover {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.dialog-actions {
  padding: 16px;
}

.save-button {
  background: linear-gradient(0deg, rgba(255, 185, 26, 1) 0%, rgba(255, 185, 26, 0.8) 100%);
  color: #0d2c6d;
  font-weight: bold;
  border-radius: 4px;
  transition: all 0.3s ease;
}

.save-button:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  transform: translateY(-2px);
}

.cancel-button {
  color: #666;
  font-weight: 500;
  margin-right: 8px;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .dialog-header {
    font-size: 1.1rem;
  }

  .setting-title {
    font-size: 1rem;
  }

  .info-label {
    min-width: 80px;
  }
}
</style>
